import { motion } from "framer-motion";
import {
  Workflow,
  MessageSquare,
  Users,
  BrainCircuit,
  ArrowUpRight,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Card({
  testId,
  pillar,
  pillarColor,
  title,
  subtitle,
  description,
  Icon,
  className,
  visual,
}) {
  return (
    <motion.div
      data-testid={testId}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      className={`group relative overflow-hidden rounded-2xl glass p-7 md:p-10 transition-all duration-500 hover:-translate-y-1 hover:border-white/25 hover:shadow-[0_0_60px_rgba(99,102,241,0.18)] ${className}`}
    >
      {/* radial hover glow */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-indigo-500/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
      {visual}
      <div className="relative">
        <div className="flex items-center gap-2 mb-5">
          <span
            className="text-[10px] font-bold uppercase tracking-[0.25em]"
            style={{ color: pillarColor }}
          >
            {pillar}
          </span>
          <div className="h-px flex-1 bg-white/10" />
          <Icon className="w-4 h-4 text-slate-400" />
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-white">
          {title}
        </h3>
        <p className="font-display text-sm uppercase tracking-[0.25em] text-indigo-300/80 mt-1">
          {subtitle}
        </p>
        <p className="mt-5 text-slate-300 text-sm md:text-base leading-relaxed max-w-md">
          {description}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/80 group-hover:text-white">
          Learn more
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </motion.div>
  );
}

export default function ServiceHub() {
  return (
    <section
      data-testid="service-hub"
      id="services"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-14 md:mb-20"
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-400">
            Two engines. One growth platform.
          </span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-white">
            A bento of capabilities for
            <span className="text-gradient"> complex enterprises.</span>
          </h2>
          <p className="mt-5 text-slate-300 text-lg max-w-2xl">
            Whether you need to automate operations or place mission-critical
            talent — we deliver both, from the same partner.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <Card
            testId="bento-card-automation"
            className="md:col-span-7"
            pillar="AI Solutions · 01"
            pillarColor="#a5b4fc"
            title="Intelligent Automation"
            subtitle="Workflows · Systems · Agents"
            description="We design and deploy end-to-end automation across your back office, sales and operations. From document processing to multi-step agentic workflows that act in real time."
            Icon={Workflow}
            visual={
              <svg viewBox="0 0 200 100" className="absolute right-0 top-0 w-2/3 h-full opacity-40 group-hover:opacity-60 transition-opacity" aria-hidden="true">
                <g fill="none" stroke="#6366f1" strokeWidth="0.6">
                  <path d="M0 50 Q50 10 100 50 T200 50" />
                  <path d="M0 70 Q50 30 100 70 T200 70" strokeOpacity="0.5" />
                  <path d="M0 30 Q50 -10 100 30 T200 30" strokeOpacity="0.3" />
                </g>
              </svg>
            }
          />
          <Card
            testId="bento-card-conversational"
            className="md:col-span-5"
            pillar="AI Solutions · 02"
            pillarColor="#a5b4fc"
            title="Conversational AI"
            subtitle="Voice · Chatbots · Agents"
            description="Custom voice agents and chat assistants trained on your data, integrated with your stack — 24/7 support, qualification and outbound at human-grade quality."
            Icon={MessageSquare}
            visual={
              <div className="absolute right-6 top-6 flex flex-col gap-2 opacity-50 group-hover:opacity-80 transition-opacity">
                <div className="w-28 h-2 rounded bg-indigo-400/30" />
                <div className="w-20 h-2 rounded bg-indigo-400/20 ml-2" />
                <div className="w-24 h-2 rounded bg-indigo-400/40" />
              </div>
            }
          />
          <Card
            testId="bento-card-talent"
            className="md:col-span-5"
            pillar="RecruitSteps · 03"
            pillarColor="#c4b5fd"
            title="Talent Acquisition"
            subtitle="Specialized Sourcing"
            description="Embedded recruiters for aerospace, banking, solar and IT. We source vetted, niche experts — from engineers to executives — fast."
            Icon={Users}
            visual={
              <div className="absolute right-6 top-6 grid grid-cols-3 gap-1.5 opacity-40 group-hover:opacity-70 transition-opacity">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-3 h-3 rounded-full"
                    style={{
                      background:
                        i % 3 === 0 ? "#6366f1" : "rgba(165,180,252,0.4)",
                    }}
                  />
                ))}
              </div>
            }
          />
          <Card
            testId="bento-card-ai-hiring"
            className="md:col-span-7"
            pillar="RecruitSteps · 04"
            pillarColor="#c4b5fd"
            title="AI-Driven Hiring"
            subtitle="Tech-Optimized Placements"
            description="We pair human recruiters with proprietary AI screening to dramatically reduce time-to-hire — predictive matching, automated qualification, bias-aware shortlists."
            Icon={BrainCircuit}
            visual={
              <svg viewBox="0 0 200 100" className="absolute right-0 top-0 w-2/3 h-full opacity-30 group-hover:opacity-60 transition-opacity" aria-hidden="true">
                <g fill="#6366f1">
                  {Array.from({ length: 24 }).map((_, i) => (
                    <circle key={i} cx={20 + (i % 8) * 22} cy={20 + Math.floor(i / 8) * 28} r={i % 5 === 0 ? 3 : 1.5} />
                  ))}
                </g>
              </svg>
            }
          />
        </div>
      </div>
    </section>
  );
}
